/*
 * The bank account withdrawals are paid to. Shown masked (GET via the wallet's
 * payout_account); added or replaced with PUT /payout-account. The full
 * account number is typed twice and never shown back: the server only ever
 * returns the last four digits.
 */

import { useState } from "react";
import { errorMessage, wallet as walletApi, type PayoutAccountMasked } from "../api/client";
import type { Notify } from "./bits";
import { fmtDate } from "./format";
import { Landmark, Lock, Plus } from "./icons";

const IFSC = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const ACCOUNT = /^\d{6,20}$/;
const UPI = /^[\w.-]{2,}@[A-Za-z][\w.-]*$/;

type Form = { holder: string; number: string; confirm: string; ifsc: string; bank: string; upi: string };

export default function BankAccountCard({
  account,
  partnerName,
  notify,
  onSaved,
}: {
  account: PayoutAccountMasked | null;
  partnerName: string;
  notify: Notify;
  onSaved: (a: PayoutAccountMasked) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Form>(() => blank(account, partnerName));
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    let v = e.target.value;
    if (k === "number" || k === "confirm") v = v.replace(/\D/g, "");
    if (k === "ifsc") v = v.toUpperCase().replace(/[^A-Z0-9]/g, "");
    setForm((f) => ({ ...f, [k]: v }));
  };

  const errors = {
    holder: !form.holder.trim() ? "Enter the name on the bank account." : null,
    number: !ACCOUNT.test(form.number) ? "Account numbers are 6 to 20 digits." : null,
    confirm: form.confirm !== form.number ? "The two account numbers don't match." : null,
    ifsc: !IFSC.test(form.ifsc) ? "IFSC is 11 characters, like HDFC0001234 (5th is a zero)." : null,
    upi: form.upi.trim() && !UPI.test(form.upi.trim()) ? "UPI IDs look like name@bank." : null,
  };
  const invalid = Object.values(errors).some(Boolean);

  function open() {
    setForm(blank(account, partnerName));
    setTouched(false);
    setError(null);
    setEditing(true);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (invalid) return;
    setBusy(true);
    setError(null);
    try {
      const saved = await walletApi.savePayoutAccount({
        account_holder: form.holder.trim(),
        account_number: form.number,
        ifsc: form.ifsc,
        ...(form.bank.trim() ? { bank_name: form.bank.trim() } : {}),
        ...(form.upi.trim() ? { upi_id: form.upi.trim() } : {}),
      });
      onSaved(saved);
      setEditing(false);
      notify(account ? "Bank account updated." : "Bank account added. You can now withdraw.");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const err = (k: keyof typeof errors) => (touched ? errors[k] : null);

  return (
    <section className="card card--pad bank-card" aria-labelledby="bank-title">
      <div className="card-heading">
        <div>
          <p className="eyebrow">Payouts</p>
          <h2 id="bank-title">Bank account</h2>
        </div>
        {!editing && account && (
          <button className="button button--secondary button--sm" onClick={open}>
            Change
          </button>
        )}
      </div>

      {editing ? (
        <form className="bank-form" onSubmit={save} noValidate>
          <div className="form-grid">
            <Field id="bk-holder" label="Account holder name" error={err("holder")} wide>
              <input id="bk-holder" className="input" autoComplete="name" value={form.holder} onChange={set("holder")} aria-invalid={Boolean(err("holder"))} data-autofocus />
            </Field>
            <Field id="bk-number" label="Account number" error={err("number")}>
              <input id="bk-number" className="input mono" inputMode="numeric" autoComplete="off" maxLength={20} value={form.number} onChange={set("number")} aria-invalid={Boolean(err("number"))} />
            </Field>
            <Field id="bk-confirm" label="Confirm account number" error={err("confirm")}>
              <input
                id="bk-confirm"
                className="input mono"
                inputMode="numeric"
                autoComplete="off"
                maxLength={20}
                value={form.confirm}
                onChange={set("confirm")}
                onPaste={(e) => e.preventDefault()}
                aria-invalid={Boolean(err("confirm"))}
              />
            </Field>
            <Field id="bk-ifsc" label="IFSC" error={err("ifsc")} hint="Printed on your cheque book or passbook.">
              <input id="bk-ifsc" className="input mono" autoComplete="off" maxLength={11} value={form.ifsc} onChange={set("ifsc")} aria-invalid={Boolean(err("ifsc"))} placeholder="HDFC0001234" />
            </Field>
            <Field id="bk-bank" label="Bank name" optional>
              <input id="bk-bank" className="input" autoComplete="off" value={form.bank} onChange={set("bank")} placeholder="HDFC Bank" />
            </Field>
            <Field id="bk-upi" label="UPI ID" optional error={err("upi")} wide>
              <input id="bk-upi" className="input" autoComplete="off" value={form.upi} onChange={set("upi")} aria-invalid={Boolean(err("upi"))} placeholder="name@okhdfcbank" />
            </Field>
          </div>
          <p className="card-note">
            <Lock size={12} /> The account must be in your name. Withdrawals already requested still go to the account
            they were requested to.
          </p>
          {error && <div className="form-error" role="alert">{error}</div>}
          <div className="form-actions">
            <button type="button" className="button button--secondary" onClick={() => setEditing(false)} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="button button--primary" disabled={busy}>
              {busy ? "Saving…" : "Save bank account"}
            </button>
          </div>
        </form>
      ) : account ? (
        <>
          <dl className="kv bank-kv">
            <div>
              <dt>Account holder</dt>
              <dd>{account.account_holder}</dd>
            </div>
            <div>
              <dt>Account</dt>
              <dd className="mono">••••{account.account_last4}</dd>
            </div>
            <div>
              <dt>IFSC</dt>
              <dd className="mono">{account.ifsc}</dd>
            </div>
            {account.bank_name && (
              <div>
                <dt>Bank</dt>
                <dd>{account.bank_name}</dd>
              </div>
            )}
            {account.upi_id && (
              <div>
                <dt>UPI</dt>
                <dd>{account.upi_id}</dd>
              </div>
            )}
          </dl>
          <p className="card-note">Last updated {fmtDate(account.updated_at)}.</p>
        </>
      ) : (
        <div className="bank-empty">
          <span className="empty__icon" aria-hidden="true">
            <Landmark size={18} />
          </span>
          <p>Add the bank account your commission should be paid to. You need one before you can withdraw.</p>
          <button className="button button--primary" onClick={open}>
            <Plus size={16} /> Add bank account
          </button>
        </div>
      )}
    </section>
  );
}

function blank(account: PayoutAccountMasked | null, partnerName: string): Form {
  return {
    holder: account?.account_holder ?? partnerName,
    number: "",
    confirm: "",
    ifsc: account?.ifsc ?? "",
    bank: account?.bank_name ?? "",
    upi: account?.upi_id ?? "",
  };
}

function Field({
  id,
  label,
  error,
  hint,
  optional,
  wide,
  children,
}: {
  id: string;
  label: string;
  error?: string | null;
  hint?: string;
  optional?: boolean;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={`field${wide ? " is-wide" : ""}`}>
      <label className="field__label" htmlFor={id}>
        {label} {optional && <em>(optional)</em>}
      </label>
      {children}
      {error ? <p className="field__error">{error}</p> : hint ? <p className="field__hint">{hint}</p> : null}
    </div>
  );
}
